import React, { useEffect, useRef, useState } from "react";
import {
  FaCalendarAlt,
  FaEnvelope,
  FaListUl,
  FaPaperPlane,
  FaPen,
  FaPhoneAlt,
  FaRedoAlt,
  FaTimes,
} from "react-icons/fa";

import servicesData from "../data/servicesData";
import styles from "../styles/ConsultationModal.module.css";

const API_BASE_URL = (
  process.env.REACT_APP_API_BASE_URL || "http://localhost:5000"
).replace(/\/$/, "");

const initialValues = {
  email: "",
  phone: "",
  requirement: "",
  description: "",
};

const initialSubmitStatus = {
  type: "",
  message: "",
};

const countCharacters = (value) => Array.from(value).length;

const limitCharacters = (value, maxCharacters) =>
  Array.from(value).slice(0, maxCharacters).join("");

const limitWords = (value, maxWords) => {
  const matches = [...value.matchAll(/\S+/g)];

  if (matches.length <= maxWords) {
    return value;
  }

  const lastAllowed = matches[maxWords - 1];

  return value.slice(0, lastAllowed.index + lastAllowed[0].length);
};

const isValidEmail = (value) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());

export default function ConsultationModal({ isOpen, onClose }) {
  const [formValues, setFormValues] = useState(initialValues);
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(initialSubmitStatus);

  const emailRef = useRef(null);
  const dialogRef = useRef(null);

  const characterCount = countCharacters(formValues.description);

  useEffect(() => {
    if (!isOpen) {
      return undefined;
    }

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    setSubmitStatus(initialSubmitStatus);

    const focusTimer = window.setTimeout(() => {
      emailRef.current?.focus();
    }, 0);

    const handleKeyDown = (event) => {
      if (event.key === "Escape" && !isSubmitting) {
        onClose();
      }
    };

    document.addEventListener("keydown", handleKeyDown);

    return () => {
      window.clearTimeout(focusTimer);
      document.body.style.overflow = previousOverflow;
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [isOpen, isSubmitting, onClose]);

  if (!isOpen) {
    return null;
  }

  const validateForm = () => {
    const nextErrors = {};

    if (!formValues.email.trim()) {
      nextErrors.email = "Email is required.";
    } else if (!isValidEmail(formValues.email)) {
      nextErrors.email = "Enter a valid email address.";
    }

    setErrors(nextErrors);

    return Object.keys(nextErrors).length === 0;
  };

  const handleFieldChange = (event) => {
    const { name, value } = event.target;
    let nextValue = value;

    if (name === "phone") {
      nextValue = value.replace(/[^0-9+\-()\s]/g, "").slice(0, 20);
    }

    if (name === "description") {
      nextValue = limitCharacters(value, 500);
    }

    setFormValues((current) => ({
      ...current,
      [name]: nextValue,
    }));

    setErrors((current) => ({
      ...current,
      [name]: "",
    }));

    setSubmitStatus(initialSubmitStatus);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    setSubmitStatus(initialSubmitStatus);

    if (!validateForm()) {
      return;
    }

    try {
      setIsSubmitting(true);

      const response = await fetch(`${API_BASE_URL}/api/consultation`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          email: formValues.email.trim(),
          phone: formValues.phone.trim(),
          requirement: formValues.requirement,
          description: formValues.description.trim(),
        }),
      });

      const result = await response.json();

      if (!response.ok) {
        if (result.field) {
          setErrors((current) => ({
            ...current,
            [result.field]: result.message,
          }));
        }

        throw new Error(
          result.message || "Unable to send the consultation request.",
        );
      }

      setFormValues(initialValues);
      setErrors({});

      setSubmitStatus({
        type: "success",
        message:
          result.message ||
          "Your consultation request has been sent successfully.",
      });
    } catch (error) {
      console.error("Consultation submission error:", error);

      setSubmitStatus({
        type: "error",
        message:
          error.message || "Unable to send your request. Please try again.",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleReset = () => {
    if (isSubmitting) {
      return;
    }

    setFormValues(initialValues);
    setErrors({});
    setSubmitStatus(initialSubmitStatus);
    emailRef.current?.focus();
  };

  const handleOverlayClick = (event) => {
    if (event.target === event.currentTarget && !isSubmitting) {
      onClose();
    }
  };

  return (
    <div className={styles.overlay} onMouseDown={handleOverlayClick}>
      <div
        ref={dialogRef}
        className={styles.modalCard}
        role="dialog"
        aria-modal="true"
        aria-labelledby="consultation-modal-title"
        aria-describedby="consultation-modal-description"
        onMouseDown={(event) => event.stopPropagation()}
      >
        <button
          type="button"
          className={styles.closeButton}
          aria-label="Close consultation form"
          onClick={onClose}
          disabled={isSubmitting}
        >
          <FaTimes aria-hidden="true" />
        </button>

        <div className={styles.headerIcon} aria-hidden="true">
          <FaCalendarAlt />
        </div>

        <header className={styles.modalHeader}>
          <h2 id="consultation-modal-title">Book a Free Consultation</h2>

          <p id="consultation-modal-description">
            Tell us about your project and we&apos;ll get back to you shortly.
          </p>
        </header>

        <form className={styles.form} onSubmit={handleSubmit} noValidate>
          <div className={styles.formGroup}>
            <label htmlFor="consultation-email">
              Email
              <span className={styles.requiredMark} aria-hidden="true">
                *
              </span>
            </label>

            <div className={styles.fieldShell}>
              <FaEnvelope className={styles.fieldIcon} aria-hidden="true" />

              <input
                ref={emailRef}
                id="consultation-email"
                name="email"
                type="email"
                value={formValues.email}
                onChange={handleFieldChange}
                placeholder="Enter your email address"
                required
                disabled={isSubmitting}
                aria-invalid={Boolean(errors.email)}
                aria-describedby={
                  errors.email ? "consultation-email-error" : undefined
                }
              />
            </div>

            {errors.email ? (
              <p className={styles.errorMessage} id="consultation-email-error">
                {errors.email}
              </p>
            ) : null}
          </div>

          <div className={styles.formGroup}>
            <label htmlFor="consultation-phone">Phone No</label>

            <div className={styles.fieldShell}>
              <FaPhoneAlt className={styles.fieldIcon} aria-hidden="true" />

              <input
                id="consultation-phone"
                name="phone"
                type="tel"
                value={formValues.phone}
                onChange={handleFieldChange}
                placeholder="Enter your phone number"
                maxLength="20"
                disabled={isSubmitting}
              />
            </div>
          </div>

          <div className={styles.formGroup}>
            <label htmlFor="consultation-requirement">Requirement</label>

            <div className={styles.fieldShell}>
              <FaListUl className={styles.fieldIcon} aria-hidden="true" />

              <select
                id="consultation-requirement"
                name="requirement"
                value={formValues.requirement}
                onChange={handleFieldChange}
                disabled={isSubmitting}
              >
                <option value="">Select your requirement</option>

                {servicesData.map((service) => (
                  <option value={service.title} key={service.id}>
                    {service.title}
                  </option>
                ))}

                <option value="Other">Other</option>
              </select>
            </div>
          </div>

          <div className={styles.formGroup}>
            <label htmlFor="consultation-description">
              Describe your project
            </label>

            <div className={`${styles.fieldShell} ${styles.textareaShell}`}>
              <FaPen className={styles.fieldIcon} aria-hidden="true" />

              <textarea
                id="consultation-description"
                name="description"
                value={formValues.description}
                onChange={handleFieldChange}
                placeholder="Tell us more about your project..."
                disabled={isSubmitting}
                maxLength={500}
              />
            </div>

            <p className={styles.wordCounter}>
              {characterCount} / 500 characters
            </p>
          </div>

          {submitStatus.message ? (
            <p
              className={
                submitStatus.type === "success"
                  ? styles.successMessage
                  : styles.submitError
              }
              role={submitStatus.type === "success" ? "status" : "alert"}
              aria-live="polite"
            >
              {submitStatus.message}
            </p>
          ) : null}

          <div className={styles.actions}>
            <button
              type="submit"
              className={styles.sendButton}
              disabled={isSubmitting}
            >
              <FaPaperPlane aria-hidden="true" />

              {isSubmitting ? "Sending..." : "Send"}
            </button>

            <button
              type="button"
              className={styles.resetButton}
              onClick={handleReset}
              disabled={isSubmitting}
            >
              <FaRedoAlt aria-hidden="true" />
              Reset
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
